import React, { useEffect, useRef, useState } from 'react';
import type { IconType } from 'react-icons';
import { FaGlobe, FaXTwitter } from 'react-icons/fa6';
import {
    SiInstagram,
    SiSpotify,
    SiYoutube,
    SiFacebook,
    SiLinkedin,
    SiTiktok,
    SiBandcamp,
    SiSoundcloud,
    SiApplemusic,
} from 'react-icons/si';
import type { ImageSource } from 'ts-core';
import { isYouTubeUrl, extractYouTubeId, isInstagramUrl, getInstagramEmbedUrl } from '../MediaForm/imageUtils';
import { Lightbox } from '../EventDetails/Lightbox';

/**
 * Minimal media-item shape shared by the user (ArtistMediaItem) and partner
 * (PartnerMediaItem) profiles — enough to classify + render a photo/video tile.
 */
export interface MediaTileItem {
    id?: string;
    url: string;
    type?: string;
    text?: string | null;
    copyright?: string | null;
}

export interface SocialLinkItem {
    platform: string;
    url: string;
}

/** Brand icon per social/streaming platform (keys match the profile editor's platform values). */
export const SOCIAL_ICONS: Record<string, IconType> = {
    website: FaGlobe,
    instagram: SiInstagram,
    spotify: SiSpotify,
    youtube: SiYoutube,
    facebook: SiFacebook,
    linkedin: SiLinkedin,
    tiktok: SiTiktok,
    bandcamp: SiBandcamp,
    soundcloud: SiSoundcloud,
    apple_music: SiApplemusic,
    x: FaXTwitter,
};

export function isVideoUrl(url: string): boolean {
    return /\.(mp4|webm|mov|ogg)(\?.*)?$/i.test(url);
}

/** True when the media item should render as an embed/video rather than a photo. */
export function isVideoItem(item: MediaTileItem): boolean {
    return (
        item.type === 'youtube' ||
        item.type === 'video' ||
        item.type === 'instagram' ||
        isYouTubeUrl(item.url) ||
        isInstagramUrl(item.url) ||
        isVideoUrl(item.url)
    );
}

export function PhotoTile({ item, title, onClick }: { item: MediaTileItem; title: string; onClick?: () => void }) {
    return (
        <img
            src={item.url}
            alt={item.text || title}
            loading="lazy"
            onClick={onClick}
            className={`w-full aspect-square object-cover rounded-lg bg-gray-100${onClick ? ' cursor-pointer' : ''}`}
        />
    );
}

/**
 * Instagram's /embed iframe can't be auto-sized cross-origin — we intentionally
 * don't load Instagram's embed.js (it pulls in Meta's tracking script), and the
 * raw embed never posts its height. Its content is a roughly square media area
 * plus fixed-height header/footer chrome, so deriving the height from the
 * rendered width keeps the box tight at any column width instead of leaving the
 * empty space a fixed height caused at narrow (2-col mobile) widths. Reels/IGTV
 * carry an extra "Add a comment" row, so they need a slightly larger offset.
 */
function InstagramTile({ url, title }: { url: string; title: string }) {
    const embed = getInstagramEmbedUrl(url);
    const ref = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number>();
    // Offsets are the header+footer chrome height (the media area letterboxes to
    // ~square, so it scales with width). Reels/IGTV show an extra "Add a comment"
    // row; tuned against live embeds at 2-col mobile width.
    const chrome = /\/(reel|reels|tv)\//i.test(url) ? 250 : 165;

    useEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === 'undefined') {
            return;
        }
        const observer = new ResizeObserver(() => {
            const width = el.clientWidth;
            if (width > 0) {
                setHeight(Math.round(width + chrome));
            }
        });
        observer.observe(el);

        return () => observer.disconnect();
    }, [chrome]);

    if (!embed) {
        return null;
    }

    return (
        <div ref={ref} className="w-full rounded-lg overflow-hidden bg-white border border-gray-200">
            <iframe
                src={embed}
                title={title}
                className="w-full block"
                style={{ height: height ?? 560, border: 0 }}
                scrolling="no"
                allow="encrypted-media; picture-in-picture"
                allowFullScreen
                loading="lazy"
            />
        </div>
    );
}

export function VideoTile({ item, title }: { item: MediaTileItem; title: string }) {
    if (item.type === 'instagram' || isInstagramUrl(item.url)) {
        return <InstagramTile url={item.url} title={title} />;
    }
    if (item.type === 'youtube' || isYouTubeUrl(item.url)) {
        const id = extractYouTubeId(item.url);
        if (!id) return null;
        return (
            <div className="aspect-video rounded-lg overflow-hidden bg-black">
                <iframe
                    src={`https://www.youtube.com/embed/${id}`}
                    title={title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            </div>
        );
    }
    return (
        <video
            src={item.url}
            controls
            playsInline
            className="w-full aspect-video object-cover rounded-lg bg-gray-100"
        />
    );
}

/** Brand-icon social link chips, shared by the user + partner profile panes. */
export function SocialChips({ links }: { links: SocialLinkItem[] }) {
    if (!links?.length) return null;
    return (
        <div className="flex flex-wrap gap-2">
            {links.map((link, i) => {
                const Icon = SOCIAL_ICONS[link.platform] ?? FaGlobe;
                return (
                    <a
                        key={i}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1 text-sm rounded-full bg-gray-100 text-gray-700 hover:bg-balloon hover:text-white transition-colors capitalize"
                    >
                        <Icon className="w-3.5 h-3.5" aria-hidden />
                        {link.platform.replace('_', ' ')}
                    </a>
                );
            })}
        </div>
    );
}

/** Photos grid + videos grid, shared by the user + partner profile panes. */
export function MediaSections({ media, title }: { media: MediaTileItem[]; title: string }) {
    const photos = (media ?? []).filter((m) => !isVideoItem(m));
    const videos = (media ?? []).filter((m) => isVideoItem(m));
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

    // Clicking a photo opens the shared event-detail Lightbox over all photos.
    const lightboxImages: ImageSource[] = photos.map((p) => ({
        url: p.url,
        text: p.text ?? undefined,
        copyright: p.copyright ?? undefined,
    }));

    return (
        <>
            {lightboxIndex !== null && (
                <Lightbox
                    images={lightboxImages}
                    initialIndex={lightboxIndex}
                    onClose={() => setLightboxIndex(null)}
                />
            )}
            {photos.length > 0 && (
                <section>
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Photos</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {photos.map((item, i) => (
                            <PhotoTile key={item.id ?? `p${i}`} item={item} title={title} onClick={() => setLightboxIndex(i)} />
                        ))}
                    </div>
                </section>
            )}
            {videos.length > 0 && (
                <section>
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Videos</h2>
                    <div className="grid grid-cols-2 gap-4">
                        {videos.map((item, i) => (
                            <VideoTile key={item.id ?? `v${i}`} item={item} title={title} />
                        ))}
                    </div>
                </section>
            )}
        </>
    );
}
